import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";
import { SEOHead } from "@/components/seo/SEOHead";

const comparisons = [
  { feature: "Lead Sourcing", aiveliq: true, competitor: false },
  { feature: "Engagement Engine", aiveliq: true, competitor: true },
  { feature: "Forecasting", aiveliq: true, competitor: false },
  { feature: "AI Coaching", aiveliq: true, competitor: false },
  { feature: "Objection Handling", aiveliq: true, competitor: false },
  { feature: "Workflow Automation", aiveliq: true, competitor: "partial" },
  { feature: "360° Analytics", aiveliq: true, competitor: "partial" },
];

export default function VsOutreach() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Aiveliq360 vs Outreach | AI Sales OS vs Sales Engagement Platform"
        description="Explore the differences between Aiveliq360 and Outreach. See why an AI Sales Operating System delivers deeper automation, intelligence, and revenue acceleration."
      />

      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/"><img src={aiveliqLogo} alt="Aiveliq360" className="h-10 object-contain" /></Link>
          <Link to="/auth"><Button size="sm" className="gap-1.5">Get Started<ArrowRight className="h-3.5 w-3.5" /></Button></Link>
        </div>
      </header>

      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-4xl font-bold mb-6">
            Aiveliq360 vs Outreach:{" "}
            <span className="gradient-text">Which Platform Drives Real Sales Acceleration?</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12">
            Outreach is a sales engagement platform. 
            Aiveliq360 is a full AI Sales OS with end-to-end automation.
          </p>

          <div className="glass rounded-xl overflow-hidden mb-12">
            <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 font-semibold">
              <div className="text-left">Feature</div>
              <div>Aiveliq360</div>
              <div>Outreach</div>
            </div>
            {comparisons.map((row) => (
              <div key={row.feature} className="grid grid-cols-3 gap-4 p-4 border-t border-border">
                <div className="text-left text-sm">{row.feature}</div>
                <div>
                  {row.aiveliq ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  )}
                </div>
                <div>
                  {row.competitor === true ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  ) : row.competitor === "partial" ? (
                    <span className="text-xs text-yellow-500">Partial</span>
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="glass rounded-xl p-6 mb-8">
            <h2 className="font-semibold mb-3">Bottom Line</h2>
            <p className="text-muted-foreground text-sm">
              Outreach helps reps send emails. 
              Aiveliq360 <strong>drives the entire GTM motion autonomously</strong>.
            </p>
          </div>

          <Link to="/auth">
            <Button size="lg" className="gap-2 ai-glow">
              Replace Fragmented Tools with Aiveliq360
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to Home</Link>
          <p>© 2025 Aiveliq360</p>
        </div>
      </footer>
    </div>
  );
}
